import { Injectable, OnDestroy } from '@angular/core';
import { Observable, of, timer } from 'rxjs';
import { catchError, finalize, map, retry, shareReplay } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../login/auth/auth.service';

export const PYTHON_TOKEN_KEY = 'pythonToken';
export const PYTHON_TOKEN_EXPIRY_KEY = 'pythonTokenExpiry';

/**
 * Owns the lifecycle of the Python (forecast) API token: acquisition with retry/backoff,
 * de-duplication of concurrent requests, proactive refresh before expiry and on-demand
 * reacquisition so a transient failure does not leave the session without a token.
 */
@Injectable({ providedIn: 'root' })
export class PythonTokenService implements OnDestroy {
  static readonly MAX_RETRIES = 3;
  static readonly BASE_RETRY_DELAY_MS = 1000;
  static readonly MAX_RETRY_DELAY_MS = 30000;
  // Refresh this long before expiry.
  static readonly REFRESH_LEAD_MS = 60000;
  static readonly FAILURE_NOTIFY_COOLDOWN_MS = 60000;

  private inFlight$: Observable<string | null> | null = null;
  private refreshTimeout: any;
  private lastFailureNotifiedAt = 0;
  private recoveringFromFailure = false;

  constructor(private auth: AuthService, private toastr: ToastrService) {}

  /** Startup check: reuse a stored valid token (scheduling its refresh) or acquire a new one. */
  init(): void {
    if (this.hasValidToken()) {
      this.scheduleRefresh(sessionStorage.getItem(PYTHON_TOKEN_EXPIRY_KEY));
    } else {
      this.acquire().subscribe();
    }
  }

  getStoredToken(): string | null {
    const token = sessionStorage.getItem(PYTHON_TOKEN_KEY);
    return token && token !== 'undefined' ? token : null;
  }

  hasValidToken(): boolean {
    return !!this.getStoredToken() && !this.getExpiryStatus(sessionStorage.getItem(PYTHON_TOKEN_EXPIRY_KEY)).isExpired;
  }

  /** Emits a usable token, acquiring one on demand when missing/expired. Emits null if recovery fails. */
  getValidToken(): Observable<string | null> {
    return this.hasValidToken() ? of(this.getStoredToken()) : this.acquire();
  }

  /**
   * Requests a new token with exponential backoff. Concurrent callers share a single request.
   * Never errors: emits the token, or null after all retries fail (and the user is notified).
   */
  acquire(force = false): Observable<string | null> {
    if (this.inFlight$) {
      return this.inFlight$;
    }
    if (force) {
      this.clearToken();
    }
    this.inFlight$ = this.auth.getPythonToken().pipe(
      retry({
        count: PythonTokenService.MAX_RETRIES,
        delay: (_err, attempt) => timer(this.getRetryDelay(attempt))
      }),
      map(data => {
        if (!data || !data.token) {
          throw new Error('Python token response did not contain a token');
        }
        this.storeToken(data.token, data.exp);
        if (this.recoveringFromFailure) {
          this.recoveringFromFailure = false;
          this.toastr.success('Connection to the forecast service has been restored.');
        }
        return data.token as string;
      }),
      catchError(error => {
        console.log('Error fetching Python token:', error);
        this.clearToken();
        this.notifyFailure();
        return of(null);
      }),
      finalize(() => {
        this.inFlight$ = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    return this.inFlight$;
  }

  getRetryDelay(attempt: number): number {
    return Math.min(
      PythonTokenService.BASE_RETRY_DELAY_MS * Math.pow(2, Math.max(attempt - 1, 0)),
      PythonTokenService.MAX_RETRY_DELAY_MS
    );
  }

  getExpiryStatus(expiry: number | string | null): { refreshTime: number; isExpired: boolean } {
    const expiryNum = Number(expiry);
    if (expiry === null || isNaN(expiryNum) || expiryNum <= 0) {
      return { refreshTime: 0, isExpired: true };
    }
    const refreshTime = expiryNum * 1000 - Date.now() - PythonTokenService.REFRESH_LEAD_MS;
    return { refreshTime, isExpired: refreshTime <= 0 };
  }

  clearToken(): void {
    sessionStorage.removeItem(PYTHON_TOKEN_KEY);
    sessionStorage.removeItem(PYTHON_TOKEN_EXPIRY_KEY);
  }

  ngOnDestroy(): void {
    this.cancelRefresh();
  }

  private storeToken(token: string, exp: number | string): void {
    sessionStorage.setItem(PYTHON_TOKEN_KEY, token);
    sessionStorage.setItem(PYTHON_TOKEN_EXPIRY_KEY, String(exp));
    this.scheduleRefresh(exp);
  }

  private scheduleRefresh(expiry: number | string | null): void {
    this.cancelRefresh();
    const { refreshTime } = this.getExpiryStatus(expiry);
    this.refreshTimeout = setTimeout(() => {
      this.refreshTimeout = null;
      this.acquire(true).subscribe();
    }, Math.max(refreshTime || 0, 0));
  }

  private cancelRefresh(): void {
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
      this.refreshTimeout = null;
    }
  }

  private notifyFailure(): void {
    this.recoveringFromFailure = true;
    const now = Date.now();
    if (now - this.lastFailureNotifiedAt < PythonTokenService.FAILURE_NOTIFY_COOLDOWN_MS) {
      return;
    }
    this.lastFailureNotifiedAt = now;
    this.toastr.error(
      'Unable to authenticate with the forecast service. Forecast data may not load; please try again shortly.',
      'Forecast service unavailable'
    );
  }
}
